import { useCallback, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import type { ConditionSearchHit } from "../types";
import { usePageSetup } from "../hooks/usePageSetup";
import { useShortcuts } from "../hooks/useShortcuts";
import { PageHeader } from "../components/PageHeader";
import { Button } from "../components/Button";
import { ConditionSearch } from "../components/condition/ConditionSearch";
import { NewConditionForm } from "../components/actuarial/NewConditionForm";
import { NewRatingForm } from "../components/actuarial/NewRatingForm";
import { editorPath } from "../utils/actuarialPaths";

type NewMode = "condition" | "rating";

const MODE_PARAM = "mode";
const MODES: readonly { value: NewMode; label: string; description: string }[] = [
  { value: "rating", label: "New rating on an existing condition", description: "Add a custom rating (with its own debits) to a condition already in the catalog." },
  { value: "condition", label: "Whole new condition", description: "A condition the guidelines do not have, created together with its first rating." },
];

/** New Custom Condition: either a brand-new condition or a new rating on an existing one. */
export function NewCustomizationPage() {
  usePageSetup("New Custom Condition", null);
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const mode: NewMode = params.get(MODE_PARAM) === "condition" ? "condition" : "rating";
  const [picked, setPicked] = useState<ConditionSearchHit | null>(null);
  const [prefillName, setPrefillName] = useState("");

  const backToList = useCallback(() => navigate("/uw/actuarial/conditions"), [navigate]);
  const shortcuts = useMemo(() => ({ cancel: backToList }), [backToList]);
  useShortcuts(shortcuts);

  const setMode = (next: NewMode) => {
    setPicked(null);
    setParams(next === "rating" ? {} : { [MODE_PARAM]: next }, { replace: true });
  };

  return (
    <>
      <PageHeader
        title="New Custom Condition"
        subtitle="Custom items are marked in search and reach underwriters and Skynet immediately."
        actions={
          <Button variant="secondary" onClick={backToList}>
            Back to customizations
          </Button>
        }
      />
      <div className="space-y-5">
        <div role="radiogroup" aria-label="What to create" className="grid gap-3 md:grid-cols-2">
          {MODES.map((option) => {
            const isChecked = mode === option.value;
            return (
              <button key={option.value} type="button" role="radio" aria-checked={isChecked} onClick={() => setMode(option.value)} className={`cursor-pointer rounded-2xl border p-4 text-left shadow-sm focus:outline-none focus:ring-2 focus:ring-brand-green-50 ${isChecked ? "border-brand-green bg-brand-green-05" : "border-gray-200 bg-white hover:bg-gray-50"}`}>
                <span className="block font-semibold text-gray-800">{option.label}</span>
                <span className="block text-sm text-gray-600">{option.description}</span>
              </button>
            );
          })}
        </div>
        {mode === "condition" && <NewConditionForm key={prefillName} initialName={prefillName} onCreated={(condition) => navigate(editorPath({ conditionId: condition.id, ratingId: null }))} onOpenExisting={(conditionId) => navigate(editorPath({ conditionId, ratingId: null }))} onCancel={backToList} />}
        {mode === "rating" && !picked && (
          <ConditionSearch
            onPick={setPicked}
            onCreate={(name) => {
              setPrefillName(name);
              setMode("condition");
            }}
          />
        )}
        {mode === "rating" && picked && <NewRatingForm condition={picked} onSaved={(rating) => navigate(editorPath({ conditionId: picked.conditionId, ratingId: rating.id }))} onChangeCondition={() => setPicked(null)} />}
      </div>
    </>
  );
}
